import React, {PropsWithChildren} from 'react';
import {NavigateFunction, useNavigate, useParams} from 'react-router-dom';
import {Box, Button} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {Panel} from '../../../components/Panel/Panel';
import {useAccountContext} from '../../../contexts/AccountContext';
import Loading from '../../../components/Loading/Loading';
import {HandoverStep} from '../../../components/HandoverStep/HandoverStep';
import {HandoverFooterNavigation} from '../../../components/FooterNavigation/HandoverFooterNavigation';
import {themeColors} from '../../../theme/Theme';
import {useReviewContext} from '../../../contexts/ReviewContext';
import useTitle from '../../../hooks/useTitle';
import {ShowIfDebugWrapper} from '../../../contexts/DebugProvider';
import {RecommendsGrid} from '../RecommendsGrid/RecommendsGrid';
import DisabledWrapper from '../../../components/DisabledWrapper/DisabledWrapper';
import {useFeaturePermission} from '../../../contexts/FeatureBoundary';
import {GlobalPermissionEnum} from '../../../schema-types/globalTypes';

type props = {word?: 'requirement' | 'instruction'};

export const onNewRecommendFactory = ({navigate}: {navigate: NavigateFunction}) => {
  return () => {
    navigate({pathname: `../recommends-builder`, search: window.location.search});
  };
};

export const ClientRecommends: React.FC<PropsWithChildren<props>> = ({
  word = 'requirement',
  children,
}) => {
  const {accountId} = useParams();
  const {account} = useAccountContext();
  const {reviewId} = useReviewContext();
  const navigate = useNavigate();
  const onNewRecommend = onNewRecommendFactory({navigate});
  const {isFullySubmitted} = useReviewContext();
  const hasDentistBuilderPerm = useFeaturePermission([
    GlobalPermissionEnum.can_access_dentist_recommendation_builder,
  ]);

  return (
    <>
      <Panel
        color={themeColors.chartPurple}
        title={`Current ${word === 'requirement' ? 'requirements' : 'instructions'}`}
      >
        <ShowIfDebugWrapper debugKey="forms">Review id: {reviewId}</ShowIfDebugWrapper>
        <Box mb={1} display="flex" justifyContent="space-between" mt={1}>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={onNewRecommend}
              disabled={isFullySubmitted}
            >
              New {word}
            </Button>
            {hasDentistBuilderPerm && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate({pathname: `../builder`})}
              >
                Go to dentist builder
              </Button>
            )}
          </Box>
          {children}
        </Box>
        {account?.id || accountId ? (
          <RecommendsGrid entityId={account?.id || accountId} />
        ) : (
          <Loading />
        )}
      </Panel>
    </>
  );
};

export const ClientPreviousRecommends: React.FC<{readOnly: boolean} & props> = ({
  readOnly,
  word = 'requirement',
}) => {
  const {accountId} = useParams();
  const {account} = useAccountContext();
  const {reviewId} = useReviewContext();
  return (
    <>
      <Panel
        color={themeColors.yellow}
        title={`Previous ${word === 'requirement' ? 'requirements' : 'instructions'}`}
      >
        <ShowIfDebugWrapper debugKey="forms">Review id: {reviewId}</ShowIfDebugWrapper>

        {account?.id || accountId ? (
          <RecommendsGrid
            readOnly
            entityId={account?.id || accountId}
            showOnlyOtherReviews
          />
        ) : (
          <Loading />
        )}
      </Panel>
    </>
  );
};

export const ClientRecommendsHandoverPage: React.FC = () => {
  const {reviewDetails} = useReviewContext();
  useTitle(`Review requirements - ${reviewDetails?.account?.name}`);
  return (
    <>
      <HandoverStep />
      <DisabledWrapper>
        <ClientRecommends />
      </DisabledWrapper>
      {/* this prevents the footer from obscuring the contents above */}
      <div style={{height: '4rem'}} />
      <HandoverFooterNavigation nextIcon={<AssessmentIcon />} />
    </>
  );
};
